<template>
    <section>
        <SideBar />
        <TopBar />
        <div class="content">

            <div class="mb-9">
                <div class="row justify-content-between align-items-end g-3 mb-5">
                    <div class="col-12 col-sm-auto col-xl-10">
                        <h2 class="mb-0">Sliders</h2>
                        <p class="text-muted mb-0 small">Arrastra una o más imágenes para añadirlas al carousel.</p>
                    </div>
                </div>

                <div
                    class="dropzone p-5 mb-4 text-center"
                    :class="{ 'dropzone--over': dragActive }"
                    @click="$refs.fileInput.click()"
                    @dragover.prevent="dragActive = true"
                    @dragleave.prevent="dragActive = false"
                    @drop.prevent="onDrop"
                    role="button"
                    tabindex="0"
                    @keydown.enter.prevent="$refs.fileInput.click()"
                >
                    <CloudUploadOutlineIcon :size="48" class="text-muted mb-2" />
                    <p class="mb-1"><strong>Arrastra imágenes aquí</strong> o haz click para seleccionar</p>
                    <p class="text-muted small mb-0">JPG / PNG / WEBP · máx 3MB por archivo · puedes seleccionar varias</p>
                    <input
                        ref="fileInput"
                        type="file"
                        class="d-none"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        multiple
                        @change="onFilesPicked"
                    />
                </div>

                <div v-if="queue.length > 0" class="card mb-4">
                    <div class="card-header d-flex justify-content-between align-items-center py-2">
                        <span class="small text-muted">{{ queueDoneCount }} de {{ queue.length }} subidas</span>
                        <button class="btn btn-sm btn-link" @click="clearQueue" :disabled="uploading">Limpiar lista</button>
                    </div>
                    <ul class="list-group list-group-flush">
                        <li v-for="(q, i) in queue" :key="'q-' + i" class="list-group-item d-flex align-items-center">
                            <img :src="q.preview" alt="" class="me-3" style="width:48px;height:48px;object-fit:cover;background:#f4f4f4;border-radius:4px" />
                            <div class="flex-grow-1 min-width-0">
                                <div class="text-truncate" :title="q.file.name">{{ q.file.name }}</div>
                                <div class="small text-muted">{{ formatSize(q.file.size) }}</div>
                                <div v-if="q.status === 'uploading'" class="progress mt-1" style="height:4px">
                                    <div class="progress-bar progress-bar-striped progress-bar-animated" style="width:100%"></div>
                                </div>
                            </div>
                            <span class="ms-3">
                                <span v-if="q.status === 'pending'" class="badge bg-secondary">Pendiente</span>
                                <span v-else-if="q.status === 'uploading'" class="badge bg-info">Subiendo…</span>
                                <span v-else-if="q.status === 'done'" class="badge bg-success">Subida</span>
                                <span v-else-if="q.status === 'error'" class="badge bg-danger" :title="q.error">Error</span>
                            </span>
                        </li>
                    </ul>
                </div>

                <div class="card" id="table">
                    <div class="card-header py-2">
                        <h5 class="mb-0">Sliders existentes ({{ sliders.length }})</h5>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm fs--1 mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Imagen</th>
                                    <th>Creado</th>
                                    <th class="text-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="est in sliders" :key="est.id">
                                    <td>
                                        <img class="rounded-soft" :src="$url + '/' + est.image" alt="" style="width:200px;background:#f4f4f4" />
                                    </td>
                                    <td class="text-muted small">
                                        #{{ est.id }}<br>
                                        {{ est.createdAt }}
                                    </td>
                                    <td class="text-end">
                                        <button class="btn btn-sm btn-outline-danger" @click="borrar(est)" :disabled="deletingId === est.id">
                                            <span v-if="deletingId === est.id">…</span>
                                            <span v-else>Eliminar</span>
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="sliders.length === 0">
                                    <td colspan="3" class="text-center text-muted py-4">Aún no hay sliders. Sube una imagen arriba para empezar.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </div>
    </section>
</template>

<script>
import SideBar from '@/components/SideBarNav.vue'
import TopBar from '@/components/TopBarNav.vue'
import axios from 'axios'
import CloudUploadOutlineIcon from 'vue-material-design-icons/CloudUploadOutline.vue'

const MAX_BYTES = 3_000_000
const ALLOWED = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp']

export default {
    name: 'SlidersView',
    components: { SideBar, TopBar, CloudUploadOutlineIcon },
    data() {
        return {
            sliders: [],
            queue: [],
            dragActive: false,
            uploading: false,
            deletingId: null
        }
    },
    computed: {
        queueDoneCount() {
            return this.queue.filter(q => q.status === 'done' || q.status === 'error').length
        }
    },
    created() {
        this.getData()
    },
    beforeDestroy() {
        this.queue.forEach(q => q.preview && URL.revokeObjectURL(q.preview))
    },
    methods: {
        formatSize(n) {
            if (!n) return ''
            if (n < 1024) return n + ' B'
            if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
            return (n / (1024 * 1024)).toFixed(2) + ' MB'
        },
        getData() {
            axios.get(this.$url + '/sliders', {
                headers: { 'Authorization': this.$store.state.token }
            }).then((res) => {
                this.sliders = res.data
            }).catch((err) => {
                console.log(err)
            })
        },
        onFilesPicked(event) {
            const files = Array.from(event.target.files || [])
            event.target.value = ''
            this.enqueueAndUpload(files)
        },
        onDrop(event) {
            this.dragActive = false
            const files = Array.from(event.dataTransfer?.files || [])
            this.enqueueAndUpload(files)
        },
        enqueueAndUpload(files) {
            const accepted = []
            for (const f of files) {
                if (!ALLOWED.includes(f.type)) {
                    this.$notify({ group: 'iasc', title: 'Archivo rechazado', text: `${f.name}: tipo no permitido`, type: 'danger' })
                    continue
                }
                if (f.size > MAX_BYTES) {
                    this.$notify({ group: 'iasc', title: 'Archivo rechazado', text: `${f.name}: supera 3MB`, type: 'danger' })
                    continue
                }
                accepted.push({
                    file: f,
                    preview: URL.createObjectURL(f),
                    status: 'pending',
                    error: null
                })
            }
            if (accepted.length === 0) return
            this.queue.push(...accepted)
            this.processQueue()
        },
        async processQueue() {
            if (this.uploading) return
            this.uploading = true
            try {
                for (const q of this.queue) {
                    if (q.status !== 'pending') continue
                    q.status = 'uploading'
                    try {
                        const fm = new FormData()
                        fm.append('foto', q.file)
                        await axios.post(this.$url + '/sliders', fm, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': this.$store.state.token
                            }
                        })
                        q.status = 'done'
                    } catch (err) {
                        q.status = 'error'
                        q.error = err?.response?.data?.error || 'Error al subir'
                        this.$notify({ group: 'iasc', title: 'Error', text: `${q.file.name}: ${q.error}`, type: 'danger' })
                    }
                }
                this.getData()
            } finally {
                this.uploading = false
            }
        },
        clearQueue() {
            this.queue.forEach(q => q.preview && URL.revokeObjectURL(q.preview))
            this.queue = []
        },
        async borrar(data) {
            if (!window.confirm(`¿Eliminar el slider #${data.id}?`)) return
            this.deletingId = data.id
            try {
                await axios.post(this.$url + '/sliders/delete/' + data.id, {}, {
                    headers: { 'Authorization': this.$store.state.token }
                })
                this.sliders = this.sliders.filter(e => e.id !== data.id)
                this.$notify({ group: 'iasc', title: 'Eliminado', text: `Slider #${data.id} eliminado`, type: 'success' })
            } catch (err) {
                this.$notify({ group: 'iasc', title: 'Error', text: 'No se pudo eliminar', type: 'danger' })
            } finally {
                this.deletingId = null
            }
        }
    }
}
</script>

<style scoped>
.dropzone {
    border: 2px dashed #c8c8c8;
    border-radius: 12px;
    background: #fafafa;
    transition: background-color .15s ease, border-color .15s ease;
    cursor: pointer;
}
.dropzone:hover,
.dropzone:focus-visible {
    border-color: #6c757d;
    background: #f1f1f1;
    outline: none;
}
.dropzone--over {
    border-color: #0d6efd;
    background: #e7f1ff;
}
.min-width-0 {
    min-width: 0;
}
</style>
